import React from "react";
import classNames from "classnames";

import { Themes } from "custom-types/Themes";

import Spinner from "components/Icons/Spinner";
import IconSvg from "components/IconSvg";
import LB from "components/LinkBase";
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
const LinkBase = LB as any;

const variants = {
  primary: "primary",
  secondary: "secondary",
  tertiary: "tertiary",
  text: "text",
} as const;

type ButtonVariant = keyof typeof variants;

export type ButtonProps = {
  className?: string;
  iconFilePath?: string;
  size: "full" | "small";
  theme?: Themes.light;
  variant: ButtonVariant;
  width?: "full";
  href?: string;
  isLoading?: boolean;
  loadingText?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  iconDimensions?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
} & Record<string, any>;
/**
 * Buttons are used to indicate actions, interactions, or links.
 *
 * **Primary** buttons are used to indicate a primary call to action. There should be one per page at most.
 *
 * **Tertiary** buttons can be used when there are two 'primary' calls to action in a single section, or occasionally to open navigation menus.
 *
 * **Text** buttons don't look like typical buttons — they're just bold, underlined text.
 *
 * **Small** buttons are usually used for groups of buttons.
 */
const Button = React.forwardRef<
  HTMLInputElement | HTMLAnchorElement | HTMLSpanElement | HTMLButtonElement,
  React.PropsWithChildren<ButtonProps>
>(
  (
    {
      className,
      iconFilePath,
      size,
      theme,
      variant,
      width,
      children,
      loadingText,
      onClick,
      disabled,
      iconDimensions,
      isLoading = false,
      ...others
    },
    ref
  ) => {
    const classes = classNames(
      "button",
      { "button--primary": variant === variants.primary },
      // secondary intentionally omitted; it is the default style of button
      { "button--tertiary": variant === variants.tertiary },
      { "button--text": variant === variants.text },
      { "button--small": size === "small" },
      { "button--full": width === "full" || size === "full" },
      { "button--light": theme === Themes.light },
      { "flex justify-center items-center": !!iconFilePath },
      className
    );

    const iconMargin = size === "small" ? "mr-xs" : "mr-sm";
    const iconSize =
      size === "small"
        ? { height: "16", width: "16" }
        : iconDimensions === "large"
        ? { height: "24", width: "24" }
        : { height: "20", width: "20" };

    const loadingOrDisabled = isLoading || disabled;
    return (
      <LinkBase
        onClick={loadingOrDisabled ? undefined : onClick}
        ref={ref}
        className={classes}
        disabled={loadingOrDisabled}
        {...others}
      >
        {!!iconFilePath && (
          <IconSvg
            className={iconMargin}
            {...iconSize}
            filePath={iconFilePath}
          />
        )}
        {isLoading ? (
          <div className="flex flex-row items-center">
            <span className="">{loadingText}</span>
            <div className="pl-2">
              <Spinner padding={false} width={10} height={10} />
            </div>
          </div>
        ) : (
          children
        )}
      </LinkBase>
    );
  }
);

export default Button;
